<template>
  <section
    class="game-card relative-position"
    :class="{ 'game-card__promo': promoGame, 'is-live': logoGroup === 'online' }"
  >
    <div class="base-border-radius game-card__image" :style="{ backgroundImage: `url('img/default-game.svg')` }">
      <div v-if="promoGame" class="promo-chip">{{ $t('Promotions') }}</div>

      <u-i-compressed-image
        :compressed-images="imagesSrc"
        loading="lazy"
        :classes="['show-image']"
        :draggable="false"
      />
      <div class="base-border-radius game_info ol-py-xs ol-py-md-sm">
        <div
          class="game-card__manufacturer"
          :class="{ 'game-card__name_one-word': isNameOneWord }"
          @click="onClickProvider"
        >
          {{ manufacturerName }}
        </div>
        <div
          class="game_info__button"
          :class="{
            'game-card__button--disabled': loadingRouteNavigation,
          }"
          @click="playGame"
        >
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M23.5 11.4019C25.5 12.5566 25.5 15.4434 23.5 16.5981L11.5 23.5263C9.5 24.681 7 23.2376 7 20.9282V7.07179C7 4.76239 9.5 3.31902 11.5 4.47372L23.5 11.4019Z"
              fill="white"
            />
          </svg>
        </div>
        <app-link v-if="item.hasDemo" :to="demoRoute" class="game-card__demo-button"> DEMO </app-link>
      </div>
      <pragmatic-layout v-if="dgaData" :key="dgaData.tableId" :dga-data="dgaData" />
      <evolution-layout v-else-if="isEvolutionLayoutVisible" :limits="limits" />
    </div>
    <div class="game_info__categories">
      <div v-if="isHotGame" class="game_info__category">
        <img :src="require('assets/categories-custom-img/hot.svg')" />
        <q-tooltip content-class="game-tooltip" anchor="center right" self="center left">
          <div class="info-tooltip">{{ $t('hotGame') }}</div>
        </q-tooltip>
      </div>
      <div v-if="isTournamentGame" class="game_info__category">
        <img :src="require('assets/categories-custom-img/tournament.svg')" />
        <q-tooltip content-class="game-tooltip" anchor="center right" self="center left">
          <div class="info-tooltip">{{ $t('tournamentGame') }}</div>
        </q-tooltip>
      </div>
      <div v-if="isFastCashbackGame" class="game_info__category">
        <img :src="require('assets/categories-custom-img/instant-cashback.svg')" />
        <q-tooltip content-class="game-tooltip" anchor="center right" self="center left">
          <div class="info-tooltip">{{ $t('instantCashback') }}</div>
        </q-tooltip>
      </div>
      <div v-if="isJackpotGame" class="game_info__category">
        <img :src="require('assets/categories-custom-img/diamond.svg?3')" />
        <q-tooltip content-class="game-tooltip" anchor="center right" self="center left">
          <div class="info-tooltip">{{ $t('jackpotGame') }}</div>
        </q-tooltip>
      </div>
      <div v-if="isDropsAndWinsGame" class="game_info__category">
        <img :src="require('assets/categories-custom-img/d&w.svg')" />
        <q-tooltip content-class="game-tooltip" anchor="center right" self="center left">
          <div class="info-tooltip">Drop and Wins</div>
        </q-tooltip>
      </div>
    </div>
    <div v-if="isNewGame" class="game_info__new" />
    <div class="game-card__footer">
      <div class="game-card__name">{{ item.name }}</div>
      <i
        v-if="getAuth"
        class="game-card__favorite la-heart"
        :class="item.isFavorite ? 'las' : heartStyle"
        @mouseover="changeHeartStyle('las')"
        @mouseleave="changeHeartStyle('lar')"
        @click="clickOnLike"
      />
    </div>
  </section>
</template>

<script>
import UICompressedImage from 'components/ui/UICompressedImage.vue'
import { mapActions, mapGetters } from 'vuex'
import { BASE_URL } from 'src/common/config'
import favoriteChangeStatus from 'src/apollo/mutations/favoriteChangeStatus.gql'
import { notification } from 'src/core/hooks/notifications'
import { dga } from 'src/common/websocket-dga'
import { defineAsyncComponent } from 'vue'

const OpenGame = defineAsyncComponent(() => import('components/games/OpenGame'))
const PragmaticLayout = defineAsyncComponent(() => import('components/games/game-layouts/PragmaticLayout.vue'))
const EvolutionLayout = defineAsyncComponent(() => import('components/games/game-layouts/EvolutionLayout.vue'))

import { EVOLUTION_LIMITS_GROUPS } from 'src/common/config'
import { consoleLogAPI } from 'src/common/functions'

export default {
  components: { UICompressedImage, PragmaticLayout, EvolutionLayout },
  inject: ['appModal'],
  props: {
    item: {
      type: Object,
      required: true,
    },
    promoGame: {
      type: Boolean,
      default: false,
    },
    logoGroup: {
      type: String,
      default: '',
    },
    logoSubgroup: {
      type: String,
      default: '',
    },
    favoritesGamesLength: {
      type: Number,
      default: 0,
    },
    manufacturerList: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['choose'],
  data() {
    return {
      heartStyle: 'lar',
      loadingRouteNavigation: false,
      BASE_URL: BASE_URL,
      dgaData: null,
    }
  },
  computed: {
    ...mapGetters(['isMobile', 'getAuth', 'favoritesCount']),
    logoGroupName() {
      return this.logoSubgroup || this.logoGroup
    },
    groups() {
      return this.item?.groups ?? []
    },
    isHotGame() {
      if (this.logoGroupName === 'hot') {
        return false
      }

      return this.groups.includes(5)
    },
    isTournamentGame() {
      if (this.logoGroupName === 'tournament') {
        return false
      }

      return this.$store.getters.isCompetitionGame(this.item.gameId)
    },
    isFastCashbackGame() {
      if (this.logoGroupName === 'cashback') {
        return false
      }

      return this.$store.getters.isFastCashbackGame(this.item.gameId)
    },
    isEvolutionLayoutVisible() {
      return (
        this.limits?.length &&
        (EVOLUTION_LIMITS_GROUPS.includes(this.logoSubgroup) || EVOLUTION_LIMITS_GROUPS.includes(this.logoGroup))
      )
    },
    isDropsAndWinsGame() {
      const dropsAndWinsGroupId = process.env.ENVIRONMENT === 'production' ? 45 : 83
      return this.groups.includes(dropsAndWinsGroupId)
    },
    isNewGame() {
      if (this.logoGroupName === 'new') {
        return false
      }

      return this.groups.includes(23)
    },
    isJackpotGame() {
      if (this.logoGroupName === 'jackpot') {
        return false
      }

      return this.groups.includes(98)
    },
    isNameOneWord() {
      return this.manufacturerName?.trim().indexOf(' ') === -1
    },
    currentManufacturer() {
      return this.item.manufacturer.code.toLowerCase()
    },
    demoRoute() {
      return this.getAuth
        ? {
            name: 'GamePageNew',
            query: { free: '1' },
            params: {
              manufacturer: this.currentManufacturer,
              game: this.item.slug,
            },
          }
        : {
            name: 'GamePageNew',
            params: {
              manufacturer: this.currentManufacturer,
              game: this.item.slug,
            },
          }
    },
    imagesSrc() {
      if (this.promoGame) {
        return {
          xl: this.item?.compressedImage?.webp.xl,
        }
      }
      return {
        lg: this.item?.compressedImage?.webp.lg,
      }
    },
    dgaTableID() {
      if (this.item?.pragmaticGames?.[0]?.tableId) {
        return this.item?.pragmaticGames[0]?.gameUuid
      }
      return null
    },
    manufacturerName() {
      return this.item.manufacturer.name.toUpperCase('en')
    },
    limits() {
      return this.item?.limits || []
    },
  },
  mounted() {
    this.dgaWsSubscribe()
  },
  beforeUnmount() {
    dga.unsubscribe(this.dgaTableID)
  },
  methods: {
    ...mapActions(['updateFavoritesCount']),
    changeHeartStyle(style) {
      if (this.isMobile) {
        return
      }
      this.heartStyle = style
    },
    dgaWsSubscribe() {
      if (this.dgaTableID) {
        dga.subscribe(this.dgaTableID, (data) => {
          this.dgaData = data
        })
      }
    },
    onClickProvider() {
      let code = this.item.manufacturer.code
      this.$emit('choose')

      this.$router.push({ name: 'GamesByManufacturer', params: { manufacturer: code } }).catch(() => {})
    },
    moveFromFavorite() {
      const favoritesPage = this.$route.params.group === 'favorites'
      const favoritesGamesZero = this.favoritesGamesLength === 0

      if (favoritesPage && favoritesGamesZero) {
        this.$router.replace({ name: 'GamesByGroup', params: { group: 'slot' } })
      }
    },
    clickOnLike() {
      this.moveFromFavorite()

      this.$apollo
        .mutate({
          mutation: favoriteChangeStatus,
          variables: {
            slug: this.item.slug,
          },
        })
        .then((res) => {
          const result = res.data.favoriteChangeStatus.result
          const isAdded = result === 'ADDED'
          let count = this.favoritesCount
          if (count !== null) {
            if (isAdded) {
              this.updateFavoritesCount(count + 1)
            } else {
              if (count - 1 >= 0) {
                this.updateFavoritesCount(count - 1)
              }
            }
          }
          const message = isAdded ? this.$t('favorite.added') : this.$t('favorite.removed')
          notification({ text: message })
        })
        .catch((e) => {
          consoleLogAPI(e)
          notification({ type: 'info', text: this.$t('serverErrorTryAgain') })
        })
    },
    async playGame() {
      this.appModal.isActive = false
      if (this.loadingRouteNavigation) {
        return
      }
      if (this.item.manufacturer.code === 'sgsb') {
        this.$router.push({ name: 'Sports' }).catch(() => {})
        return
      }

      this.$emit('choose')

      this.loadingRouteNavigation = true

      if (this.getAuth) {
        const query = { ...this.$route.query }
        delete query.manufacturer
        const location = {
          name: 'GamePageNew',
          query,
          params: {
            ...this.$route.params,
            manufacturer: this.item.manufacturer.code.toLowerCase(),
            game: this.item.slug,
          },
        }

        if (this.$route.name === 'GamePageNew') {
          delete location.query.free

          this.$router?.replace(location)?.catch(() => {})
          this.loadingRouteNavigation = false
          return
        }

        this.$router.push(location).catch(() => {})
        this.loadingRouteNavigation = false
      } else {
        this.appModal.open({
          componentName: OpenGame,
          props: { game: this.item },
        })
        this.loadingRouteNavigation = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.q-focus-helper {
  background: transparent;
}

.promo-chip {
  padding: 4px 8px;
  background: $positive;
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 1;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 1px 0 8px 2px rgb(0 0 0 / 80%);
}

.game_info__new {
  position: absolute;
  right: 4px;
  top: 4px;
  height: 12px;
  width: 24px;
  border-radius: 2px;
  background: white;
  box-shadow: 1px 1px 0 0 rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 0;
  @include desktop {
    height: 16px;
    width: 32px;
  }

  &:before {
    content: 'new';
    color: var(--cas-dark-900);
    font-size: 9px;
    font-style: normal;
    font-weight: 800;
    text-transform: uppercase;
    display: inline;
    @include desktop {
      font-size: 12px;
    }
  }
}

.game-card {
  &__demo-button {
    text-align: center;
    color: var(--cas-light-900);
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px; /* 114.286% */
    letter-spacing: 0.7px;
    text-transform: uppercase;
    text-decoration: none;
    width: 100%;
    position: relative;
  }

  &__footer {
    padding-top: 8px;
    padding-bottom: 4px;
    display: flex;
    justify-content: space-between;
    width: 100%;
    color: var(--cas-light-700);
  }

  &__name {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  }

  &__favorite {
    font-size: 16px;
    cursor: pointer;
  }

  &__manufacturer {
    color: var(--cas-light-700);
    font-weight: 400;
    font-size: 12px;
    text-align: center;
    margin-left: 30px;
    margin-right: 30px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 14px;
    max-height: 28px;
    cursor: pointer;

    @media (min-width: 400px) and (max-width: 1199px) {
      font-size: 12px;
      margin-left: 36px;
      margin-right: 36px;
    }

    @media (min-width: 1200px) {
      font-size: 12px;
      line-height: 16px;
    }

    &_one-word {
      display: block;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  &__image {
    border-radius: var(--border-radius-2);
    overflow: hidden;
    aspect-ratio: 3/2;
    position: relative;
    width: 100%;
    background-position: center;
    background-size: cover;

    &:hover {
      .game_info {
        transition: 0.3s;
        visibility: visible;
        opacity: 1;
      }
    }

    .game-card__promo & {
      aspect-ratio: 6/2;
    }

    .is-live & {
      aspect-ratio: 1/1;
    }

    .is-live.game-card__promo & {
      aspect-ratio: 2/1 !important;
    }

    img,
    video {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      object-fit: cover;
      object-position: center;
      border-radius: var(--radius-1);
      overflow: hidden;
      visibility: visible;
    }

    img {
      opacity: 0;

      &.show-image {
        opacity: 1;
      }
    }
  }

  .compressed-image {
    display: block;
    height: 100%;
  }

  .game_info {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    background-color: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(1.5px);
    opacity: 0;
    visibility: hidden;
    z-index: 2;
    border-radius: var(--border-radius-2);
    padding: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    pointer-events: none;

    & > * {
      pointer-events: all;
    }

    @media (min-width: 400px) and (max-width: 1199px) {
      padding: 8px;
    }

    @media (min-width: 1200px) {
      padding: 8px;
    }

    &__category {
      position: relative;
      cursor: pointer;
      width: 20px;

      img {
        width: 100%;
        pointer-events: none;
        user-select: none;
      }
    }

    &__categories {
      display: flex;
      flex-direction: column;
      width: 22px;
      position: absolute;
      left: 4px;
      top: 4px;
      gap: 4px;
      z-index: 0;
    }

    &__button {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: var(--cas-game-button);
      transition: 0.3s;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      margin: auto;

      @media (min-width: 400px) {
        width: 56px;
        height: 56px;
        top: 18px;
      }

      @media (max-width: 767px) {
        width: 40px;
        height: 40px;
      }

      svg {
        width: 22px;

        @media (min-width: 768px) {
          width: 28px;
        }
      }

      &_disabled {
        opacity: 50%;
        pointer-events: none;
        user-select: none;
      }

      &_filtered {
        background: #777;
      }

      &:hover {
        background: var(--cas-game-button-hover);
      }

      &:active {
        background: var(--cas-game-button-active);
      }
    }
  }
}
</style>
